// App
import { useSelector } from 'react-redux';

// Constellation
import { Anchor, Box, Button, Heading, Icon, ModalTrigger, Paragraph, Tooltip } from '@zillow/constellation-10';
import { IconEditOutline, IconLockClosedOutline } from '@zillow/constellation-icons';

// Types
import type { State } from 'app/shared/types/State';

// Misc / Utils
import CONSTANTS from 'app/shared/constants/ConstantsBundle';
import numberUtils from 'app/shared/utils/numberUtils';
import { emitClaimHomeEvent, emitPostListingEvent } from 'app/shared/constants/PriceMyRentalCSEvents';
import { zestimateContainerStyles } from './styles';

const ZestimateCard = ({
  rentZestimate,
  min,
  max,
  zpid,
  priceFromFilter,
}: {
  rentZestimate: number | null;
  min: number;
  max: number;
  zpid: number | null;
  priceFromFilter: number | null;
}) => {
  const user = useSelector((state: State) => state.user);
  const isUserLoggedIn = useSelector((state: State) => state.user.loggedIn);
  const userProperty = useSelector((state: State) => state.address);
  const { addressHasListing } = userProperty;

  const claimHomeURL = `https://www.zillow.com/homedetails/${zpid}_zpid/?view=owner`;
  const tooltipContent =
    "A Rent Zestimate® is Zillow's estimate of a property's monthly rental price. It is computed using a formula and is a starting point in determining the monthly rental price for a specific property.";

  const handleButtonClick = () => {
    emitPostListingEvent(user);
  };

  const handleClaimYourHomeClick = () => {
    emitClaimHomeEvent(user);
  };

  const createButtonUrl = () => {
    const { street, city, state, zip, unit, zrmListingId } = userProperty;
    const isUserPropertyAvailable = street !== null && city !== null && state !== null && zip !== null;
    let listHomeForRentURL: string = CONSTANTS.ZILLOW_LIST_HOME_FOR_RENT;

    if (isUserLoggedIn && isUserPropertyAvailable && addressHasListing) {
      listHomeForRentURL = listHomeForRentURL.replace('?postingPath=true', '/');
      listHomeForRentURL += zrmListingId + '/listing';
    } else if (isUserPropertyAvailable) {
      const streetKebabCase: string = street.trim().replace(/\s/g, '-');
      const cityKebabCase: string = city.trim().replace(/\s/g, '-');
      listHomeForRentURL += '&address=' + streetKebabCase + '-' + cityKebabCase + '-' + state + '-' + zip;
      listHomeForRentURL += unit != null ? `&unit=` + unit : '';
    }

    return listHomeForRentURL;
  };

  let useRentZestimate: boolean;

  if (rentZestimate && !priceFromFilter) {
    useRentZestimate = true;
  } else if (rentZestimate && priceFromFilter && rentZestimate === priceFromFilter) {
    useRentZestimate = true;
  } else if (priceFromFilter) {
    useRentZestimate = false;
  } else {
    useRentZestimate = false;
  }

  return (
    <Box css={zestimateContainerStyles}>
      <Heading level={3} textStyle="heading-xs">
        {useRentZestimate ? (
          <>
            Your Rent Zestimate<sup>®</sup> <Tooltip content={tooltipContent} />
          </>
        ) : (
          <>Custom rental price</>
        )}
      </Heading>
      <br />
      {useRentZestimate && (
        <ModalTrigger multiModalTriggerId="change-price-modal">
          <Box css={{ cursor: 'pointer', display: 'inline-block' }}>
            <Heading level={2} textStyle="heading-md">
              {rentZestimate && numberUtils.asCurrency(rentZestimate)}/mo
              <Icon size="lg" css={{ color: 'icon.accent.brand.hero', marginLeft: '200', marginTop: '200' }}>
                <IconEditOutline />
              </Icon>
            </Heading>
          </Box>
        </ModalTrigger>
      )}
      {!useRentZestimate && priceFromFilter && (
        <ModalTrigger multiModalTriggerId="change-price-modal">
          <Box css={{ cursor: 'pointer', display: 'inline-block' }}>
            <Heading level={2} textStyle="heading-md">
              {numberUtils.asCurrency(priceFromFilter)}/mo
              <Icon size="lg" css={{ color: 'icon.accent.brand.hero', marginLeft: '200', marginTop: '200' }}>
                <IconEditOutline />
              </Icon>
            </Heading>
          </Box>
        </ModalTrigger>
      )}
      <br />
      {useRentZestimate && min && max ? (
        <Heading level={4} css={{ textStyle: 'body' }}>
          Rent Zestimate<sup>®</sup> range:{' '}
          <strong>
            {numberUtils.asCurrency(min)} - {numberUtils.asCurrency(max)}
          </strong>
        </Heading>
      ) : null}
      <Button
        asChild
        size="sm"
        fluid
        tone="brand"
        emphasis="filled"
        onClick={handleButtonClick}
        css={{ marginBottom: '400', marginTop: '600' }}
      >
        <a href={createButtonUrl()} target="_blank" rel="noopener noreferrer">
          {addressHasListing ? 'Access your rental listing' : 'List your home for rent'}
        </a>
      </Button>
      <Box css={{ display: 'flex' }}>
        <Icon css={{ fontSize: '21px', marginRight: '100' }}>
          <IconLockClosedOutline />
        </Icon>
        <Paragraph textStyle="body-sm">
          Are you the owner?{' '}
          <Anchor onClick={handleClaimYourHomeClick} href={claimHomeURL} target="_blank">
            Claim your home
          </Anchor>{' '}
          to edit beds, baths, and square footage.
        </Paragraph>
      </Box>
    </Box>
  );
};

export default ZestimateCard;
